import { createBrowserRouter as createReactBrowserRouter, RouterProvider } from 'react-router-dom';
import { lazy } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { RedirectIfLoggedIn } from '@/components/auth/RedirectIfLoggedIn';
import { LazyPage } from '@/components/layout/LazyPage';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { createMonitoredBrowserRouter } from '@/lib/monitoring';

// ---- Auth pages (lazy loaded) ----
const LoginPage = lazy(() => import('@/app/(auth)/login/page'));
const RegisterPage = lazy(() => import('@/app/(auth)/register/page'));
const ForgotPasswordPage = lazy(() => import('@/app/(auth)/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('@/app/(auth)/reset-password/page'));
const VerifyEmailPage = lazy(() => import('@/app/(auth)/verify-email/page'));
const ConfirmEmailPage = lazy(() => import('@/app/(auth)/confirm-email/page'));
const AcceptInvitePage = lazy(() => import('@/app/(auth)/accept-invite/page'));
const SetupMfaPage = lazy(() => import('@/app/(auth)/setup-mfa/page'));
const VerifyMfaPage = lazy(() => import('@/app/(auth)/verify-mfa/page'));

// ---- Public pages ----
const HomePage = lazy(() => import('@/app/(public)/home/page'));
const GuidesPage = lazy(() => import('@/app/(public)/guides/page'));
const JoinPage = lazy(() => import('@/app/(public)/join/page'));
const PrivateBetaPage = lazy(() => import('@/app/(public)/beta/page'));
const PrivacyPage = lazy(() => import('@/app/(public)/privacy/page'));
const TermsPage = lazy(() => import('@/app/(public)/terms/page'));
const SecurityPage = lazy(() => import('@/app/(public)/security/page'));
const DesignPreviewPage = lazy(() => import('@/app/(public)/design-preview/page'));
const NotFoundPage = lazy(() => import('@/app/(public)/not-found/page'));

// ---- Employee pages ----
const EmployeeDashboard = lazy(() => import('@/app/(dashboard)/employee/dashboard/page'));
const EmployeeMyShifts = lazy(() => import('@/app/(dashboard)/employee/my-shifts/page'));
const EmployeeAvailableShifts = lazy(
  () => import('@/app/(dashboard)/employee/available-shifts/page'),
);
const EmployeeShiftDetail = lazy(() => import('@/app/(dashboard)/employee/shift/page'));
const EmployeeTimesheets = lazy(() => import('@/app/(dashboard)/employee/timesheets/page'));
const EmployeeTimesheetNew = lazy(() => import('@/app/(dashboard)/employee/timesheet-new/page'));
const EmployeeAvailability = lazy(() => import('@/app/(dashboard)/employee/availability/page'));
const EmployeeLeave = lazy(() => import('@/app/(dashboard)/employee/leave/page'));
const EmployeePayslips = lazy(() => import('@/app/(dashboard)/employee/payslips/page'));
const EmployeeInvoices = lazy(() => import('@/app/(dashboard)/employee/invoices/page'));
const EmployeeCommunication = lazy(() => import('@/app/(dashboard)/employee/communication/page'));

// ---- Manager pages ----
const ManagerDashboard = lazy(() => import('@/app/(dashboard)/manager/dashboard/page'));
const ManagerShifts = lazy(() => import('@/app/(dashboard)/manager/shifts/page'));
const ManagerCreateShift = lazy(() => import('@/app/(dashboard)/manager/create-shift/page'));
const ManagerImportShifts = lazy(() => import('@/app/(dashboard)/manager/import-shifts/page'));
const ManagerShiftDetail = lazy(() => import('@/app/(dashboard)/manager/shift/page'));
const ManagerEditShift = lazy(() => import('@/app/(dashboard)/manager/edit-shift/page'));
const ManagerRecurringSeries = lazy(
  () => import('@/app/(dashboard)/manager/recurring-series/page'),
);
const ManagerApprovals = lazy(() => import('@/app/(dashboard)/manager/approvals/page'));
const ManagerTimesheetApprovals = lazy(
  () => import('@/app/(dashboard)/manager/approvals/timesheets/page'),
);
const ManagerContractorInvoiceApprovals = lazy(
  () => import('@/app/(dashboard)/manager/approvals/contractor-invoices/page'),
);
const ManagerLeaveApprovals = lazy(() => import('@/app/(dashboard)/manager/approvals/leave/page'));
const ManagerTeam = lazy(() => import('@/app/(dashboard)/manager/team/page'));
const ManagerTeamMember = lazy(() => import('@/app/(dashboard)/manager/team-member/page'));
const ManagerTeamMemberDiary = lazy(
  () => import('@/app/(dashboard)/manager/team-member-diary/page'),
);
const ManagerCommunication = lazy(() => import('@/app/(dashboard)/manager/communication/page'));
const ManagerAuditLog = lazy(() => import('@/app/(dashboard)/manager/audit-log/page'));

// ---- BM pages ----
const BmDashboard = lazy(() => import('@/app/(dashboard)/bm/dashboard/page'));
const BmShifts = lazy(() => import('@/app/(dashboard)/bm/shifts/page'));
const BmShiftDetail = lazy(() => import('@/app/(dashboard)/bm/shift/page'));
const BmApprovals = lazy(() => import('@/app/(dashboard)/bm/approvals/page'));
const BmTimesheetApprovals = lazy(() => import('@/app/(dashboard)/bm/approvals/timesheets/page'));
const BmContractorInvoiceApprovals = lazy(
  () => import('@/app/(dashboard)/bm/approvals/contractor-invoices/page'),
);
const BmLeaveApprovals = lazy(() => import('@/app/(dashboard)/bm/approvals/leave/page'));
const BmEmployees = lazy(() => import('@/app/(dashboard)/bm/employees/page'));
const BmManagers = lazy(() => import('@/app/(dashboard)/bm/managers/page'));
const BmReports = lazy(() => import('@/app/(dashboard)/bm/reports/page'));
const BmCommunication = lazy(() => import('@/app/(dashboard)/bm/communication/page'));
const BmInvoices = lazy(() => import('@/app/(dashboard)/bm/invoices/page'));
const BmTeam = lazy(() => import('@/app/(dashboard)/bm/team/page'));
const BmTeamMember = lazy(() => import('@/app/(dashboard)/bm/team-member/page'));
const BmTeamMemberDiary = lazy(() => import('@/app/(dashboard)/bm/team-member-diary/page'));
const BmPermissions = lazy(() => import('@/app/(dashboard)/bm/permissions/page'));
const BmAuditLog = lazy(() => import('@/app/(dashboard)/bm/audit-log/page'));

// ---- BO pages ----
const BoDashboard = lazy(() => import('@/app/(dashboard)/bo/dashboard/page'));
const BoShifts = lazy(() => import('@/app/(dashboard)/bo/shifts/page'));
const BoCreateShift = lazy(() => import('@/app/(dashboard)/bo/create-shift/page'));
const BoImportShifts = lazy(() => import('@/app/(dashboard)/bo/import-shifts/page'));
const BoShiftDetail = lazy(() => import('@/app/(dashboard)/bo/shift/page'));
const BoEditShift = lazy(() => import('@/app/(dashboard)/bo/edit-shift/page'));
const BoRecurringSeries = lazy(() => import('@/app/(dashboard)/bo/recurring-series/page'));
const BoApprovals = lazy(() => import('@/app/(dashboard)/bo/approvals/page'));
const BoTimesheetApprovals = lazy(() => import('@/app/(dashboard)/bo/approvals/timesheets/page'));
const BoContractorInvoiceApprovals = lazy(
  () => import('@/app/(dashboard)/bo/approvals/contractor-invoices/page'),
);
const BoLeaveApprovals = lazy(() => import('@/app/(dashboard)/bo/approvals/leave/page'));
const BoEmployees = lazy(() => import('@/app/(dashboard)/bo/employees/page'));
const BoManagers = lazy(() => import('@/app/(dashboard)/bo/managers/page'));
const BoReports = lazy(() => import('@/app/(dashboard)/bo/reports/page'));
const BoBilling = lazy(() => import('@/app/(dashboard)/bo/billing/page'));
const BoOffboarding = lazy(() => import('@/app/(dashboard)/bo/offboarding/page'));
const BoSettings = lazy(() => import('@/app/(dashboard)/bo/settings/page'));
const BoPermissions = lazy(() => import('@/app/(dashboard)/bo/permissions/page'));
const BoClients = lazy(() => import('@/app/(dashboard)/bo/clients/page'));
const BoClientDetail = lazy(() => import('@/app/(dashboard)/bo/client/page'));
const BoJobs = lazy(() => import('@/app/(dashboard)/bo/jobs/page'));
const BoCreateJob = lazy(() => import('@/app/(dashboard)/bo/create-job/page'));
const BoJobDetail = lazy(() => import('@/app/(dashboard)/bo/job/page'));
const BoEditJob = lazy(() => import('@/app/(dashboard)/bo/edit-job/page'));
const BoCommunication = lazy(() => import('@/app/(dashboard)/bo/communication/page'));
const BoInvoices = lazy(() => import('@/app/(dashboard)/bo/invoices/page'));
const BoInvoiceDetail = lazy(() => import('@/app/(dashboard)/bo/invoice/page'));
const BoTeam = lazy(() => import('@/app/(dashboard)/bo/team/page'));
const BoTeamMember = lazy(() => import('@/app/(dashboard)/bo/team-member/page'));
const BoTeamMemberDiary = lazy(() => import('@/app/(dashboard)/bo/team-member-diary/page'));
const BoAuditLog = lazy(() => import('@/app/(dashboard)/bo/audit-log/page'));

// ---- Admin pages ----
const AdminDashboard = lazy(() => import('@/app/(dashboard)/admin/dashboard/page'));
const AdminOrgs = lazy(() => import('@/app/(dashboard)/admin/orgs/page'));
const AdminOrgSupport = lazy(() => import('@/app/(dashboard)/admin/org-support/page'));
const AdminBillingPlans = lazy(() => import('@/app/(dashboard)/admin/billing-plans/page'));
const AdminDiscountCodes = lazy(() => import('@/app/(dashboard)/admin/discount-codes/page'));
const AdminPrivateBeta = lazy(() => import('@/app/(dashboard)/admin/private-beta/page'));
const AdminAbnLookup = lazy(() => import('@/app/(dashboard)/admin/abn-lookup/page'));
const IconPreview = lazy(() => import('@/app/(public)/icon-preview/page'));

// ---- Shared pages ----
const ProfilePage = lazy(() => import('@/app/(dashboard)/profile/page'));
const TrialExpiredPage = lazy(() => import('@/app/(dashboard)/trial-expired/page'));
const MyRecordPage = lazy(() => import('@/app/(dashboard)/my-record/page'));
const ShiftRulesSettings = lazy(() => import('@/app/(dashboard)/settings/shift-rules/page'));
const PublicHolidaysSettings = lazy(
  () => import('@/app/(dashboard)/settings/public-holidays/page'),
);
const RosterDigestSettings = lazy(() => import('@/app/(dashboard)/settings/roster-digest/page'));

const createBrowserRouter = createMonitoredBrowserRouter(createReactBrowserRouter);

const router = createBrowserRouter([
  // ---- Public routes ----
  {
    path: '/',
    element: (
      <LazyPage>
        <HomePage />
      </LazyPage>
    ),
  },
  {
    path: '/privacy',
    element: (
      <LazyPage>
        <PrivacyPage />
      </LazyPage>
    ),
  },
  // Development only: a place to look at the icon set in context before adopting it.
  // Spread so it contributes nothing at all to a production build.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/icon-preview',
          element: (
            <LazyPage>
              <IconPreview />
            </LazyPage>
          ),
        },
      ]
    : []),
  {
    path: '/terms',
    element: (
      <LazyPage>
        <TermsPage />
      </LazyPage>
    ),
  },
  {
    path: '/security',
    element: (
      <LazyPage>
        <SecurityPage />
      </LazyPage>
    ),
  },
  {
    path: '/guides/:slug?',
    element: (
      <LazyPage><GuidesPage /></LazyPage>
    ),
  },
  {
    path: '/design-preview',
    element: (
      <LazyPage>
        <DesignPreviewPage />
      </LazyPage>
    ),
  },

  // ---- Auth routes ----
  {
    element: <RedirectIfLoggedIn />,
    children: [
      {
        path: '/join',
        element: (
          <LazyPage>
            <JoinPage />
          </LazyPage>
        ),
      },
      {
        path: '/beta',
        element: (
          <LazyPage>
            <PrivateBetaPage />
          </LazyPage>
        ),
      },
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            element: (
              <LazyPage>
                <LoginPage />
              </LazyPage>
            ),
          },
          {
            path: '/register',
            element: (
              <LazyPage>
                <RegisterPage />
              </LazyPage>
            ),
          },
          {
            path: '/forgot-password',
            element: (
              <LazyPage>
                <ForgotPasswordPage />
              </LazyPage>
            ),
          },
          {
            path: '/reset-password',
            element: (
              <LazyPage>
                <ResetPasswordPage />
              </LazyPage>
            ),
          },
          {
            path: '/verify-email',
            element: (
              <LazyPage>
                <VerifyEmailPage />
              </LazyPage>
            ),
          },
          {
            path: '/confirm-email',
            element: (
              <LazyPage>
                <ConfirmEmailPage />
              </LazyPage>
            ),
          },
          {
            path: '/accept-invite',
            element: (
              <LazyPage>
                <AcceptInvitePage />
              </LazyPage>
            ),
          },
          {
            path: '/setup-mfa',
            element: (
              <LazyPage>
                <SetupMfaPage />
              </LazyPage>
            ),
          },
          {
            path: '/verify-mfa',
            element: (
              <LazyPage>
                <VerifyMfaPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },

  // ---- Dashboard routes (authenticated) ----
  {
    element: <OnboardingGuard />,
    children: [
      {
        element: <DashboardLayout />,
        errorElement: (
          <ErrorBoundary>
            <div />
          </ErrorBoundary>
        ),
        children: [
          // Shared
          {
            path: '/my-record',
            element: (
              <LazyPage>
                <MyRecordPage />
              </LazyPage>
            ),
          },
          {
            path: '/profile',
            element: (
              <LazyPage>
                <ProfilePage />
              </LazyPage>
            ),
          },
          {
            path: '/trial-expired',
            element: (
              <LazyPage>
                <TrialExpiredPage />
              </LazyPage>
            ),
          },

          // Employee routes
          {
            element: <ProtectedRoute allowedRoles={['EMPLOYEE']} />,
            children: [
              {
                path: '/employee/dashboard',
                element: (
                  <LazyPage>
                    <EmployeeDashboard />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/my-shifts',
                element: (
                  <LazyPage>
                    <EmployeeMyShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/available-shifts',
                element: (
                  <LazyPage>
                    <EmployeeAvailableShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/shift/:id',
                element: (
                  <LazyPage>
                    <EmployeeShiftDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/timesheets',
                element: (
                  <LazyPage>
                    <EmployeeTimesheets />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/timesheet/new',
                element: (
                  <LazyPage>
                    <EmployeeTimesheetNew />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/availability',
                element: (
                  <LazyPage>
                    <EmployeeAvailability />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/leave',
                element: (
                  <LazyPage>
                    <EmployeeLeave />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/communication',
                element: (
                  <LazyPage>
                    <EmployeeCommunication />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/payslips',
                element: (
                  <LazyPage>
                    <EmployeePayslips />
                  </LazyPage>
                ),
              },
              {
                path: '/employee/invoices',
                element: (
                  <LazyPage>
                    <EmployeeInvoices />
                  </LazyPage>
                ),
              },
            ],
          },

          // Manager routes
          {
            element: <ProtectedRoute allowedRoles={['MANAGER']} />,
            children: [
              {
                path: '/manager/dashboard',
                element: (
                  <LazyPage>
                    <ManagerDashboard />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/shifts',
                element: (
                  <LazyPage>
                    <ManagerShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/shifts/create',
                element: (
                  <LazyPage>
                    <ManagerCreateShift />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/shifts/import',
                element: (
                  <LazyPage>
                    <ManagerImportShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/shift/:id',
                element: (
                  <LazyPage>
                    <ManagerShiftDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/shift/:id/edit',
                element: (
                  <LazyPage>
                    <ManagerEditShift />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/recurring-series',
                element: (
                  <LazyPage>
                    <ManagerRecurringSeries />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/approvals',
                element: (
                  <LazyPage>
                    <ManagerApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/approvals/timesheets',
                element: (
                  <LazyPage>
                    <ManagerTimesheetApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/approvals/contractor-invoices',
                element: (
                  <LazyPage>
                    <ManagerContractorInvoiceApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/approvals/leave',
                element: (
                  <LazyPage>
                    <ManagerLeaveApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/team',
                element: (
                  <LazyPage>
                    <ManagerTeam />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/team/:memberId',
                element: (
                  <LazyPage>
                    <ManagerTeamMember />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/team/:memberId/diary',
                element: (
                  <LazyPage>
                    <ManagerTeamMemberDiary />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/communication',
                element: (
                  <LazyPage>
                    <ManagerCommunication />
                  </LazyPage>
                ),
              },
              {
                path: '/manager/audit-log',
                element: (
                  <LazyPage>
                    <ManagerAuditLog />
                  </LazyPage>
                ),
              },
            ],
          },

          // BM routes
          {
            element: <ProtectedRoute allowedRoles={['BM']} />,
            children: [
              {
                path: '/bm/dashboard',
                element: (
                  <LazyPage>
                    <BmDashboard />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/shifts',
                element: (
                  <LazyPage>
                    <BmShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/shift/:id',
                element: (
                  <LazyPage>
                    <BmShiftDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/approvals',
                element: (
                  <LazyPage>
                    <BmApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/approvals/timesheets',
                element: (
                  <LazyPage>
                    <BmTimesheetApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/approvals/contractor-invoices',
                element: (
                  <LazyPage>
                    <BmContractorInvoiceApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/approvals/leave',
                element: (
                  <LazyPage>
                    <BmLeaveApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/employees',
                element: (
                  <LazyPage>
                    <BmEmployees />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/managers',
                element: (
                  <LazyPage>
                    <BmManagers />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/reports',
                element: (
                  <LazyPage>
                    <BmReports />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/communication',
                element: (
                  <LazyPage>
                    <BmCommunication />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/invoices',
                element: (
                  <LazyPage>
                    <BmInvoices />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/team',
                element: (
                  <LazyPage>
                    <BmTeam />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/team/:memberId',
                element: (
                  <LazyPage>
                    <BmTeamMember />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/team/:memberId/diary',
                element: (
                  <LazyPage>
                    <BmTeamMemberDiary />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/settings/shift-rules',
                element: (
                  <LazyPage>
                    <ShiftRulesSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/settings/public-holidays',
                element: (
                  <LazyPage>
                    <PublicHolidaysSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/settings/roster-email',
                element: (
                  <LazyPage>
                    <RosterDigestSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/permissions',
                element: (
                  <LazyPage>
                    <BmPermissions />
                  </LazyPage>
                ),
              },
              {
                path: '/bm/audit-log',
                element: (
                  <LazyPage>
                    <BmAuditLog />
                  </LazyPage>
                ),
              },
            ],
          },

          // BO routes
          {
            element: <ProtectedRoute allowedRoles={['BO']} />,
            children: [
              {
                path: '/bo/dashboard',
                element: (
                  <LazyPage>
                    <BoDashboard />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/shifts',
                element: (
                  <LazyPage>
                    <BoShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/shifts/create',
                element: (
                  <LazyPage>
                    <BoCreateShift />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/shifts/import',
                element: (
                  <LazyPage>
                    <BoImportShifts />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/shift/:id',
                element: (
                  <LazyPage>
                    <BoShiftDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/shift/:id/edit',
                element: (
                  <LazyPage>
                    <BoEditShift />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/recurring-series',
                element: (
                  <LazyPage>
                    <BoRecurringSeries />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/approvals',
                element: (
                  <LazyPage>
                    <BoApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/approvals/timesheets',
                element: (
                  <LazyPage>
                    <BoTimesheetApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/approvals/contractor-invoices',
                element: (
                  <LazyPage>
                    <BoContractorInvoiceApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/approvals/leave',
                element: (
                  <LazyPage>
                    <BoLeaveApprovals />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/employees',
                element: (
                  <LazyPage>
                    <BoEmployees />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/managers',
                element: (
                  <LazyPage>
                    <BoManagers />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/reports',
                element: (
                  <LazyPage>
                    <BoReports />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/billing',
                element: (
                  <LazyPage>
                    <BoBilling />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/offboarding',
                element: (
                  <LazyPage>
                    <BoOffboarding />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/settings',
                element: (
                  <LazyPage>
                    <BoSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/settings/shift-rules',
                element: (
                  <LazyPage>
                    <ShiftRulesSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/settings/public-holidays',
                element: (
                  <LazyPage>
                    <PublicHolidaysSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/settings/roster-email',
                element: (
                  <LazyPage>
                    <RosterDigestSettings />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/permissions',
                element: (
                  <LazyPage>
                    <BoPermissions />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/clients',
                element: (
                  <LazyPage>
                    <BoClients />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/clients/:id',
                element: (
                  <LazyPage>
                    <BoClientDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/jobs',
                element: (
                  <LazyPage>
                    <BoJobs />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/jobs/create',
                element: (
                  <LazyPage>
                    <BoCreateJob />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/job/:id',
                element: (
                  <LazyPage>
                    <BoJobDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/job/:id/edit',
                element: (
                  <LazyPage>
                    <BoEditJob />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/communication',
                element: (
                  <LazyPage>
                    <BoCommunication />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/invoices',
                element: (
                  <LazyPage>
                    <BoInvoices />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/invoices/:id',
                element: (
                  <LazyPage>
                    <BoInvoiceDetail />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/team',
                element: (
                  <LazyPage>
                    <BoTeam />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/team/:memberId',
                element: (
                  <LazyPage>
                    <BoTeamMember />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/team/:memberId/diary',
                element: (
                  <LazyPage>
                    <BoTeamMemberDiary />
                  </LazyPage>
                ),
              },
              {
                path: '/bo/audit-log',
                element: (
                  <LazyPage>
                    <BoAuditLog />
                  </LazyPage>
                ),
              },
            ],
          },

          // Admin routes
          {
            element: <ProtectedRoute allowedRoles={['ADMIN']} />,
            children: [
              {
                path: '/admin/dashboard',
                element: (
                  <LazyPage>
                    <AdminDashboard />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/orgs',
                element: (
                  <LazyPage>
                    <AdminOrgs />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/orgs/:organizationId',
                element: (
                  <LazyPage>
                    <AdminOrgSupport />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/billing-plans',
                element: (
                  <LazyPage>
                    <AdminBillingPlans />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/discount-codes',
                element: (
                  <LazyPage>
                    <AdminDiscountCodes />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/private-beta',
                element: (
                  <LazyPage>
                    <AdminPrivateBeta />
                  </LazyPage>
                ),
              },
              {
                path: '/admin/abn-lookup',
                element: (
                  <LazyPage>
                    <AdminAbnLookup />
                  </LazyPage>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // ---- Catch-all ----
  {
    path: '*',
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
